(() => {
  const selector = "[data-bbmc-image-slider]";
  const articleSelector = ".article--content";
  const lightboxTriggerClass = "bbmc-lightbox-trigger";
  const detailImagePattern = /(install|wiring|wire\b|fuse|mount|cable|routing|trim|hardwir|grommet|fuse ?box|panel removal|footage|sample image|screen ?shot|app interface|desktop software|diagram|comparison|chart|table|layout|guidance|speed class|capacity|settings|front camera|rear camera|interior camera|connector|battery pack|step\s+\d)/i;
  const decorativeImagePattern = /(logo|icon|badge|banner|hero|spacer|divider|decoration)/i;

  let lightbox;

  const initializeSlider = (slider) => {
    if (slider.dataset.bbmcImageSliderReady === "true") return;

    const viewport = slider.querySelector("[data-bbmc-image-slider-viewport]");
    const slides = Array.from(slider.querySelectorAll("[data-bbmc-image-slider-slide]"));
    const previous = slider.querySelector("[data-bbmc-image-slider-previous]");
    const next = slider.querySelector("[data-bbmc-image-slider-next]");
    const current = slider.querySelector("[data-bbmc-image-slider-current]");
    const total = slider.querySelector("[data-bbmc-image-slider-total]");

    if (!viewport || slides.length < 3 || !previous || !next || !current || !total) return;

    slider.dataset.bbmcImageSliderReady = "true";
    total.textContent = String(slides.length);

    let activeIndex = 0;
    let frame;

    const updateStatus = (index) => {
      activeIndex = Math.max(0, Math.min(index, slides.length - 1));
      current.textContent = String(activeIndex + 1);
      slides.forEach((slide, slideIndex) => {
        if (slideIndex === activeIndex) {
          slide.setAttribute("aria-current", "true");
        } else {
          slide.removeAttribute("aria-current");
        }
      });
    };

    const moveTo = (index) => {
      const wrappedIndex = (index + slides.length) % slides.length;
      const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      viewport.scrollTo({
        left: slides[wrappedIndex].offsetLeft,
        behavior: reduceMotion ? "auto" : "smooth",
      });
      updateStatus(wrappedIndex);
    };

    previous.addEventListener("click", () => moveTo(activeIndex - 1));
    next.addEventListener("click", () => moveTo(activeIndex + 1));

    viewport.addEventListener("keydown", (event) => {
      if (event.key === "ArrowLeft") {
        event.preventDefault();
        moveTo(activeIndex - 1);
      }
      if (event.key === "ArrowRight") {
        event.preventDefault();
        moveTo(activeIndex + 1);
      }
    });

    viewport.addEventListener(
      "scroll",
      () => {
        window.cancelAnimationFrame(frame);
        frame = window.requestAnimationFrame(() => {
          const nearest = slides.reduce(
            (best, slide, index) => {
              const distance = Math.abs(slide.offsetLeft - viewport.scrollLeft);
              return distance < best.distance ? { index, distance } : best;
            },
            { index: 0, distance: Number.POSITIVE_INFINITY },
          );
          updateStatus(nearest.index);
        });
      },
      { passive: true },
    );

    updateStatus(0);
  };

  const getOptimizedLightboxSource = (image) => {
    const sourceSet = image.getAttribute("srcset") || image.dataset.srcset || "";
    const candidates = sourceSet
      .split(",")
      .map((candidate) => {
        const match = candidate.trim().match(/^(.*?)\s+(\d+)w$/);
        return match ? { url: match[1], width: Number(match[2]) } : null;
      })
      .filter(Boolean)
      .sort((a, b) => a.width - b.width);

    const cappedCandidates = candidates.filter((candidate) => candidate.width <= 2000);
    const selected = cappedCandidates.at(-1) || candidates[0];
    const fallback = image.dataset.src || image.currentSrc || image.src;
    const rawSource = selected?.url || fallback;

    if (!rawSource) return "";

    try {
      const url = new URL(rawSource, window.location.href);
      if (url.hostname.endsWith("shopify.com") || url.hostname.endsWith("shopifycdn.com")) {
        url.searchParams.delete("crop");
        url.searchParams.delete("height");
        url.searchParams.set("width", "2000");
      }
      return url.href;
    } catch {
      return rawSource;
    }
  };

  const isEligibleLightboxImage = (image) => {
    if (!image.closest(articleSelector)) return false;
    if (image.closest("a")) return false;
    if (image.closest(".bbmc-article-cover")) return false;
    if (image.closest(".bbmc-product-card, .bbmc-inline-product-media")) return false;
    if (image.closest(".bbmc-image-slider")) return false;

    const alt = image.alt.trim();
    if (!alt || decorativeImagePattern.test(alt)) return false;

    return Boolean(image.closest(".bbmc-image-grid__item")) || detailImagePattern.test(alt);
  };

  const createLightbox = () => {
    if (lightbox) return lightbox;

    const dialog = document.createElement("dialog");
    dialog.className = "bbmc-lightbox";
    dialog.setAttribute("aria-label", "Image viewer");
    dialog.innerHTML = `
      <div class="bbmc-lightbox__inner">
        <button class="bbmc-lightbox__close" type="button" aria-label="Close image viewer">&times;</button>
        <button class="bbmc-lightbox__nav bbmc-lightbox__nav--previous" type="button" aria-label="Previous image">&#8249;</button>
        <figure class="bbmc-lightbox__figure">
          <img class="bbmc-lightbox__image" alt="">
          <figcaption class="bbmc-lightbox__caption"></figcaption>
        </figure>
        <button class="bbmc-lightbox__nav bbmc-lightbox__nav--next" type="button" aria-label="Next image">&#8250;</button>
      </div>
    `;
    document.body.append(dialog);

    const image = dialog.querySelector(".bbmc-lightbox__image");
    const caption = dialog.querySelector(".bbmc-lightbox__caption");
    const figure = dialog.querySelector(".bbmc-lightbox__figure");
    const close = dialog.querySelector(".bbmc-lightbox__close");
    const previous = dialog.querySelector(".bbmc-lightbox__nav--previous");
    const next = dialog.querySelector(".bbmc-lightbox__nav--next");

    const state = {
      activeTrigger: null,
      index: 0,
      triggers: [],
      pointerStartX: null,
    };

    const updateImage = (index) => {
      if (!state.triggers.length) return;

      state.index = (index + state.triggers.length) % state.triggers.length;
      const trigger = state.triggers[state.index];
      const source = getOptimizedLightboxSource(trigger);
      const alt = trigger.alt.trim();

      dialog.classList.add("is-loading");
      image.removeAttribute("src");
      image.alt = alt;
      caption.textContent = alt;
      caption.hidden = !alt;
      previous.hidden = state.triggers.length < 2;
      next.hidden = state.triggers.length < 2;

      if (source) image.src = source;
    };

    const show = (trigger) => {
      const article = trigger.closest(articleSelector);
      state.triggers = Array.from(article.querySelectorAll(`img.${lightboxTriggerClass}`));
      state.activeTrigger = trigger;
      state.index = Math.max(0, state.triggers.indexOf(trigger));
      updateImage(state.index);

      document.body.classList.add("bbmc-lightbox-open");
      if (typeof dialog.showModal === "function") {
        dialog.showModal();
      } else {
        dialog.setAttribute("open", "");
      }
      close.focus();
    };

    const hide = () => {
      if (typeof dialog.close === "function" && dialog.open) {
        dialog.close();
      } else {
        dialog.removeAttribute("open");
        document.body.classList.remove("bbmc-lightbox-open");
        state.activeTrigger?.focus();
      }
    };

    image.addEventListener("load", () => dialog.classList.remove("is-loading"));
    image.addEventListener("error", () => dialog.classList.remove("is-loading"));
    close.addEventListener("click", hide);
    previous.addEventListener("click", () => updateImage(state.index - 1));
    next.addEventListener("click", () => updateImage(state.index + 1));

    dialog.addEventListener("click", (event) => {
      if (event.target === dialog) hide();
    });

    dialog.addEventListener("close", () => {
      document.body.classList.remove("bbmc-lightbox-open");
      image.removeAttribute("src");
      state.activeTrigger?.focus();
    });

    dialog.addEventListener("keydown", (event) => {
      if (event.key === "ArrowLeft" && state.triggers.length > 1) {
        event.preventDefault();
        updateImage(state.index - 1);
      }
      if (event.key === "ArrowRight" && state.triggers.length > 1) {
        event.preventDefault();
        updateImage(state.index + 1);
      }
      if (event.key === "Escape") hide();

      if (event.key === "Tab") {
        const controls = Array.from(dialog.querySelectorAll("button:not([hidden])"));
        const first = controls[0];
        const last = controls.at(-1);
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }
    });

    figure.addEventListener("pointerdown", (event) => {
      state.pointerStartX = event.clientX;
    });

    figure.addEventListener("pointerup", (event) => {
      if (state.pointerStartX === null || state.triggers.length < 2) return;
      const distance = event.clientX - state.pointerStartX;
      state.pointerStartX = null;
      if (Math.abs(distance) < 50) return;
      updateImage(state.index + (distance < 0 ? 1 : -1));
    });

    lightbox = { dialog, show };
    return lightbox;
  };

  const initializeLightboxImage = (image) => {
    if (image.dataset.bbmcLightboxReady === "true" || !isEligibleLightboxImage(image)) return;

    image.dataset.bbmcLightboxReady = "true";
    image.classList.add(lightboxTriggerClass);
    image.setAttribute("role", "button");
    image.setAttribute("tabindex", "0");
    image.setAttribute("aria-haspopup", "dialog");
    image.setAttribute("aria-label", `Open larger image: ${image.alt.trim()}`);

    const open = () => createLightbox().show(image);
    image.addEventListener("click", open);
    image.addEventListener("keydown", (event) => {
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        open();
      }
    });
  };

  const initializeAll = (root = document) => {
    const sliders = [];
    if (root.matches?.(selector)) sliders.push(root);
    sliders.push(...root.querySelectorAll?.(selector) ?? []);
    sliders.forEach(initializeSlider);

    const articles = [];
    if (root.matches?.(articleSelector)) articles.push(root);
    articles.push(...root.querySelectorAll?.(articleSelector) ?? []);
    articles.forEach((article) => article.querySelectorAll("img").forEach(initializeLightboxImage));
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => initializeAll());
  } else {
    initializeAll();
  }

  document.addEventListener("shopify:section:load", (event) => initializeAll(event.target));
})();
